import { useState } from 'react';
import { DEFAULT_AVAILABILITY } from '@acs/shared';
import { Banner, Button, Field, Input, Modal, Select } from '@acs/ui';
import { useI18n, type TranslationKey } from '../../i18n/index.js';
import { ownerBookingRefusal } from '../../shared/api/appointments.js';
import type { AppointmentDoc } from '../../shared/api/converters.js';
import { friendlyError } from '../../shared/api/errors.js';
import { formatDateTime, formatTime } from '../../shared/lib/format.js';
import { labelToMin, minToLabel, mondayOf, todayIsoIn, zonedDateAndMinute } from '../schedule-grid/index.js';
import { AppointmentMessage } from './AppointmentMessage.js';
import { AppointmentServiceAnswers } from './AppointmentServiceAnswers.js';
import { useAvailability, useServices } from '../settings/hooks.js';
import { useStaff } from '../staff/hooks.js';
import {
  asOptions,
  blockedTimes,
  bookingGrid,
  bookingsOn,
  capacityFor,
  endOptions,
  followStart,
  isDate,
  isTime,
  outsideHours,
  rangeMin,
  slotProblem,
  slotVerdict,
  startIsGone,
  startOptions,
  timeRangeProblem,
} from './bookingForm.js';
import { CancelBookingDialog } from './CancelBookingDialog.js';
import { DiscardChangesDialog } from './DiscardChangesDialog.js';
import { useRescheduleBooking, useWeekAppointments } from './hooks.js';

/** Value of the staff select meaning "whoever is free" (doc 43 D7). */
const ANY = '';

interface TimeDraft {
  date: string;
  start: string;
  end: string;
  /** A member's id, or {@link ANY}. */
  staffId: string;
}

/**
 * A booking's details, opened from the calendar. "Change time" turns the When
 * row into staff / date / start / end inputs (doc 43, doc 85): moving the start
 * carries the end along at the same duration (D11), times the book does not
 * allow are disabled, and saving is a doc 14 move — the booking gets a new id,
 * so the dialog closes and the calendar shows it in its new place.
 */
export function BookingDialog({
  businessId,
  appt,
  timezone,
  slotStepMin,
  onClose,
}: {
  businessId: string;
  appt: AppointmentDoc;
  timezone: string;
  /** The business's booking step — the times the form offers (doc 43 D12).
   * Raw from the business document; `bookingGrid` guards a bad value. */
  slotStepMin: number;
  onClose(): void;
}) {
  const { t, tag } = useI18n();
  const move = useRescheduleBooking();
  const [confirming, setConfirming] = useState(false);
  /** Set while "discard your changes?" is up: what discarding then does. */
  const [discarding, setDiscarding] = useState<'close' | 'stopEditing' | null>(null);
  const [original] = useState<TimeDraft>(() => ({ ...timesOf(appt, timezone), staffId: appt.staffId ?? ANY }));
  const [draft, setDraft] = useState<TimeDraft | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const { data: staff } = useStaff(businessId);
  const { data: services } = useServices(businessId);
  const activeStaff = (staff ?? []).filter((s) => s.active);
  // Who may hold it: those offering its service (doc 09 D6). A member who has
  // left or stopped offering it is not a choice — the form shows "any".
  const eligibleStaff = activeStaff.filter((s) => s.serviceIds.length === 0 || s.serviceIds.includes(appt.serviceId));
  const staffOf = (d: TimeDraft) => (eligibleStaff.some((s) => s.id === d.staffId) ? d.staffId : ANY);
  const staffId = draft ? staffOf(draft) : ANY;
  const member = eligibleStaff.find((s) => s.id === staffId) ?? null;

  const sourceLabel = t(`booking.source.${appt.source}` as TranslationKey);
  const editing = draft !== null;
  const dirty =
    editing &&
    (draft.date !== original.date ||
      draft.start !== original.start ||
      draft.end !== original.end ||
      staffOf(draft) !== staffOf(original));
  const today = todayIsoIn(timezone);
  const nowMin = zonedDateAndMinute(new Date(), timezone).min;
  const timeProblem = draft ? timeRangeProblem(draft.start, draft.end) : null;
  const { data: avail } = useAvailability(businessId);
  const availability = avail ?? DEFAULT_AVAILABILITY;
  const grid = draft ? bookingGrid(slotStepMin, availability, draft.date) : null;
  const startGone = draft ? startIsGone(draft.date, draft.start, today, nowMin) : false;
  const durationMin = Math.round((appt.end.getTime() - appt.start.getTime()) / 60_000);

  // What the book allows at the new time (doc 85), this booking aside — it
  // must not block its own move. Null until the team and the bookings have
  // arrived: nothing is ruled out before that.
  const day = draft && isDate(draft.date) ? draft.date : original.date;
  const { data: appointments } = useWeekAppointments(businessId, mondayOf(day));
  const allDay = services?.find((s) => s.id === appt.serviceId)?.allDay ?? false;
  const capacity =
    !draft || staff === undefined || appointments === undefined
      ? null
      : capacityFor(
          activeStaff.length === 0 ? null : member ? [member] : eligibleStaff,
          availability,
          draft.date,
          bookingsOn(appointments, draft.date, timezone, appt.id),
        );
  const blocked = blockedTimes(capacity, draft?.start ?? '', rangeMin(draft?.start ?? '', draft?.end ?? '') || durationMin, allDay);
  const verdict =
    draft && capacity && timeProblem === null
      ? slotVerdict(capacity, labelToMin(draft.start), labelToMin(draft.end), allDay)
      : 'free';
  const slotError = slotProblem(capacity, verdict, member?.name ?? null);
  const offHours =
    !!draft &&
    capacity !== null &&
    !capacity.staffed &&
    timeProblem === null &&
    outsideHours(availability, draft.date, draft.start, draft.end, allDay);

  // The staff list must be in: saving before it would send "any" for a
  // booking that has a member.
  const canSave =
    editing && dirty && staff !== undefined && !startGone && timeProblem === null && verdict === 'free' && !move.isPending;

  const edit = (patch: Partial<TimeDraft>) => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
    setRefusal(null);
    move.reset();
  };

  /** Moving the date to today must not leave a start that has already gone
   * selected — it would only be refused on save (D6). */
  const changeDate = (date: string) => {
    if (!draft) return;
    let start = draft.start;
    if (date === today && startIsGone(date, start, today, nowMin)) {
      start = startOptions(bookingGrid(slotStepMin, availability, date), '', nowMin)[0] ?? '';
    }
    edit({ date, start, end: followStart(draft.start, draft.end, start, durationMin) });
  };

  const stopEditing = () => {
    setDraft(null);
    setRefusal(null);
    move.reset();
  };

  /** ✕, Escape, backdrop and Close: unsaved times are asked about first (D9). */
  const requestClose = () => {
    if (move.isPending) return;
    if (dirty) setDiscarding('close');
    else onClose();
  };

  const save = () => {
    if (!draft || !canSave) return;
    move.mutate(
      {
        businessId,
        appointmentId: appt.id,
        // Always a value — an absent one is an older form's "keep" (doc 85).
        staffId: staffId === ANY ? null : staffId,
        date: draft.date,
        startTime: draft.start,
        endTime: draft.end,
      },
      {
        // The toast is the hook's (see `useRescheduleBooking`); this only runs
        // if the dialog is still up.
        onSuccess: onClose,
        onError: (err) => {
          const reason = ownerBookingRefusal(err);
          if (reason) setRefusal(t(`bookingForm.refusal.${reason}` as TranslationKey));
        },
      },
    );
  };

  const footer = editing ? (
    <>
      <Button variant="ghost" onClick={dirty ? () => setDiscarding('stopEditing') : stopEditing} disabled={move.isPending}>
        {t('common.cancel')}
      </Button>
      <Button disabled={!canSave} loading={move.isPending} onClick={save}>
        {t('common.save')}
      </Button>
    </>
  ) : (
    <>
      <Button variant="ghost" onClick={onClose}>{t('booking.close')}</Button>
      {/* The calendar lists confirmed bookings only — a cancelled one leaves
          it, and this dialog with it — so there is no cancelled state here. */}
      <Button variant="secondary" onClick={() => setDraft(original)}>
        {t('bookingForm.changeTime')}
      </Button>
      <Button variant="danger" onClick={() => setConfirming(true)}>
        {t('booking.cancelConfirm')}
      </Button>
    </>
  );

  return (
    <Modal
      closeLabel={t('ui.closeDialog')}
      title={appt.serviceName}
      // Escape is a document-level listener in every overlay, so while a
      // confirmation is up it must dismiss only that — not this dialog under it.
      onClose={confirming || discarding !== null ? () => {} : requestClose}
      footer={footer}
    >
      <dl className="booking-details">
        <dt>{t('booking.when')}</dt>
        <dd>
          {formatDateTime(appt.start, timezone, tag)} – {formatTime(appt.end, timezone, tag)}
        </dd>

        <dt>{t('booking.service')}</dt>
        <dd>{appt.serviceName}</dd>

        {appt.staffName ? (
          <>
            <dt>{t('booking.staff')}</dt>
            <dd>{appt.staffName}</dd>
          </>
        ) : null}

        <dt>{t('booking.customer')}</dt>
        <dd>{appt.customerName}</dd>

        <dt>{t('booking.phone')}</dt>
        <dd className="mono">{appt.customerPhone ?? '—'}</dd>

        {appt.note ? (
          <>
            <dt>{t('bookingForm.note')}</dt>
            <dd>{appt.note}</dd>
          </>
        ) : null}

        <dt>{t('booking.bookedVia')}</dt>
        <dd>{sourceLabel}</dd>
        {/* No status row: the calendar only shows confirmed bookings and the
            owner has no status to act on — it said "confirmed" every time. */}
      </dl>

      {/* The service's questions with the customer's answers (doc 64), as
          asked when it was booked — kept in view while the time is changed. */}
      <AppointmentServiceAnswers answers={appt.serviceAnswers} />

      {/* What the caller asked the business to know or do (doc 28) — the same
          block the list view shows. */}
      {appt.message && <AppointmentMessage message={appt.message} />}

      {draft && (
        <div className="booking-form booking-form--move">
          <h3 className="booking-form__title">{t('bookingForm.newTime')}</h3>
          {activeStaff.length > 0 && (
            <Field label={t('booking.staff')}>
              <Select
                value={staffId}
                onChange={(e) => edit({ staffId: e.target.value })}
                options={[
                  { value: ANY, label: t('bookingForm.anyStaff') },
                  ...eligibleStaff.map((s) => ({ value: s.id, label: s.name })),
                ]}
              />
            </Field>
          )}
          <div className="booking-form__when">
            <Field
              label={t('bookingForm.date')}
              required
              error={draft.date && draft.date < today ? t('bookingForm.refusal.in_past') : undefined}
            >
              <Input autoFocus type="date" min={today} value={draft.date} onChange={(e) => changeDate(e.target.value)} />
            </Field>
            <Field
              label={t('bookingForm.start')}
              required
              error={
                startGone && draft.date === today
                  ? t('bookingForm.refusal.in_past')
                  : slotError
                    ? t(slotError.key, { name: slotError.name ?? '' })
                    : undefined
              }
            >
              <Select
                value={draft.start}
                placeholder="—"
                options={asOptions(startOptions(grid!, draft.start, draft.date === today ? nowMin : -1), blocked.start)}
                onChange={(e) => edit({ start: e.target.value, end: followStart(draft.start, draft.end, e.target.value, durationMin) })}
              />
            </Field>
            <Field
              label={t('bookingForm.end')}
              required
              error={
                timeProblem && (timeProblem !== 'missing' || isTime(draft.start))
                  ? t(`bookingForm.time.${timeProblem}` as TranslationKey)
                  : undefined
              }
            >
              <Select
                value={draft.end}
                placeholder="—"
                options={asOptions(endOptions(grid!, draft.start, draft.end), blocked.end)}
                onChange={(e) => edit({ end: e.target.value })}
              />
            </Field>
          </div>
          <p className="muted booking-form__hint">{t('bookingForm.moveHint')}</p>

          {offHours && <Banner tone="warning">{t('bookingForm.outsideHours')}</Banner>}
          {refusal && <Banner tone="danger">{refusal}</Banner>}
          {move.isError && !refusal && (
            <Banner tone="danger">{friendlyError(t, move.error, t('bookingForm.moveFailed'))}</Banner>
          )}
        </div>
      )}

      {confirming && (
        <CancelBookingDialog
          businessId={businessId}
          appt={appt}
          timezone={timezone}
          onClose={() => setConfirming(false)}
          onCancelled={onClose}
        />
      )}
      {discarding && (
        <DiscardChangesDialog
          onKeep={() => setDiscarding(null)}
          onDiscard={() => {
            // Cancel in the form drops the edit; ✕/Escape/backdrop close the dialog.
            if (discarding === 'close') onClose();
            else stopEditing();
            setDiscarding(null);
          }}
        />
      )}
    </Modal>
  );
}

/** The booking's date, start and end as the business's wall clock. */
function timesOf(appt: AppointmentDoc, timezone: string): Omit<TimeDraft, 'staffId'> {
  const start = zonedDateAndMinute(appt.start, timezone);
  const end = zonedDateAndMinute(appt.end, timezone);
  return {
    date: start.date,
    start: minToLabel(start.min),
    // A booking that runs past midnight has no same-day end to show; the form
    // asks for one rather than invent 23:59 and shorten it (doc 43 D6).
    end: end.date === start.date ? minToLabel(end.min) : '',
  };
}
